import React, { useEffect, useState } from "react";
import { cn } from "./cn";
import { StarIconSolid } from "../../../assets/ui/icons";

/* Company mark — the uploaded logo, or derived initials as a fallback. */
export const getInitials = (name = "") => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

/* The API sends no brand colour, so one is picked from a fixed palette by
   hashing the company's key. Same company, same colour on every render and
   every page — without needing the backend to store one. */
const PALETTE = ["#0A3A56", "#EA5539", "#1F7A5C", "#7A4FBF", "#B7791F", "#2563A8"];

export const getMarkColor = (key = "") => {
  const s = String(key || "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
};

/* Some rows carry the strings "undefined" / "null" in `logo` rather than a real
   null — present in live data. They are truthy, so without screening them here
   the mark would request a URL of literally "undefined". */
const PLACEHOLDER_LOGOS = new Set(["undefined", "null", "none", "false"]);

/* Shared with the profile masthead's banner, which has the same problem from
   the same cause: `FormData.append(field, undefined)` stringifies, so a failed
   upload wrote the literal "undefined" over a good value. See F-103. */
export const hasAssetUrl = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  !PLACEHOLDER_LOGOS.has(value.trim().toLowerCase());

export const hasLogo = hasAssetUrl;

/* Size variants rather than a pixel prop: Tailwind cannot emit a class for a
   value computed at runtime, so a fixed set keeps the whole mark in utility
   classes instead of inline geometry. */
const SIZES = {
  // Menu rows — the account switcher packs several into a scrolling list.
  xs: { box: "w-[34px] h-[34px] text-[14px]", star: "w-4 h-4 -top-[5px] -right-[5px]", glyph: 8 },
  sm: { box: "w-11 h-11 text-[15px]", star: "w-4 h-4 -top-1 -right-1", glyph: 8 },
  md: { box: "w-14 h-14 text-[19px]", star: "w-[21px] h-[21px] -top-[6px] -right-[6px]", glyph: 11 },
  lg: { box: "w-16 h-16 text-[22px]", star: "w-6 h-6 -top-[7px] -right-[7px]", glyph: 12 },
  /* The company profile's masthead, where the mark is the page's subject
     rather than a row's marker — 88px, as the design draws it beside a 27px
     company name. `lg` stays where it is for the several screens using it. */
  xl: { box: "w-[88px] h-[88px] text-[30px]", star: "w-7 h-7 -top-2 -right-2", glyph: 14 },
};

const MemberStar = ({ variant }) => (
  <span
    title="Member"
    aria-hidden="true"
    className={cn(
      "absolute rounded-full flex items-center justify-center",
      "border-2 border-white bg-[var(--gold-star)] text-white",
      "shadow-[0_1px_3px_rgba(0,0,0,.22)]",
      variant.star
    )}
  >
    <StarIconSolid size={variant.glyph} />
  </span>
);

/* The account menu draws every mark in one navy, the way the design export's ProfileMenu does: a short list of the user's own businesses reads as a set, and... */
export const BRAND_MARK = "linear-gradient(140deg, #0A3A56, #00233A)";
/* The gradient's own base, so the tile is still navy if a browser drops the
   image layer. */
export const BRAND_MARK_BASE = "#00233A";

export const CompanyMark = ({
  company = {},
  size = "md",
  member = false,
  tone = "hash",
  /* A person is drawn as a circle, a business as a rounded square. The profile
     lists both — stakeholders, signatories, personnel — so the shape is a prop
     rather than three `!important` overrides at the call site. */
  shape = "rounded",
  className = "",
}) => {
  const variant = SIZES[size] || SIZES.md;
  const name = company?.name || company?.registered_company_name || "";
  const key = company?.companyID || company?.wb_win || name;
  const initials = getInitials(name);

  /* The API returns a `logo` path on companies that have uploaded one; most of the directory is unclaimed records that have not. */
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = company?.logo;
  const showLogo = hasLogo(logo) && !logoFailed;

  // A new company in the same slot must get a fresh attempt at its own logo.
  useEffect(() => {
    setLogoFailed(false);
  }, [logo]);

  const boxClasses = cn(
    "shrink-0 border border-black/[0.04]",
    shape === "circle" ? "rounded-full" : "rounded-[10px]",
    variant.box
  );

  const mark = showLogo ? (
    <img
      src={logo}
      alt=""
      aria-hidden="true"
      onError={() => setLogoFailed(true)}
      className={cn(boxClasses, "object-cover bg-white", className)}
    />
  ) : (
    <div
      aria-hidden="true"
      className={cn(
        boxClasses,
        "flex items-center justify-center font-bold text-white tracking-[-0.02em]",
        className
      )}
      /* The one inline style left in this component: the palette colour is
         chosen per company at runtime, and Tailwind has no class for a value
         it cannot see at build time. */
      style={
        tone === "brand"
          ? { backgroundColor: BRAND_MARK_BASE, backgroundImage: BRAND_MARK }
          : { backgroundColor: getMarkColor(key) }
      }
    >
      {initials}
    </div>
  );

  if (!member) return mark;

  return (
    <div className="relative shrink-0 leading-none">
      {mark}
      <MemberStar variant={variant} />
    </div>
  );
};

export default CompanyMark;
